import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { generateStickerLabel } from '../lib/generateStickerLabel';

interface SlipResult {
  id: number;
  slipCode: string;
  jobId: string;
}

interface Printer {
  PrinterID: string;
  DisplayName: string | null;
  Location: string | null;
  LastHeartbeatOn: string | null;
}

/** Trimmed to what the sticker label + this page's preview actually use. */
interface LookupResult {
  slipCode: string;
  jobId: string;
  jobName: string | null;
  client: string | null;
  product: string | null;
  poNumber: string | null;
  poQuantity: string | null;
  itemCode: string | null;
  materialCode: string | null;
  quantity: string | null;
  slipDate: string | null;
  shift: string | null;
  operator: string | null;
  qc: string | null;
  machine: string | null;
  remark: string | null;
  location: string | null;
  createdOn: string | null;
  createdBy: string | null;
}

/**
 * Job info fetched to pre-fill the editable fields.  Whatever ends up in
 * those fields is what gets stored on the slip and printed — the operator
 * can correct a wrong client-master row or a stale item code before the
 * sticker leaves the machine.
 */
interface JobPreview {
  Job_ID: string;
  Job_Name: string | null;
  Client_Name: string | null;
  Product_Name: string | null;
  PO_Number: string | null;
  PO_Quantity: string | null;
  Total_Boxes: string | null;
  TotalSheet_withWastage: string | null;
  Product_AW_Material_No: string | null;
  Product_Ups: string | null;
  JobOrder_date: string | null;
  Job_Staus: string | null;
  Client_Address_Dispatch: string | null;
}

export default function Slips() {
  return (
    <div className="p-6 max-w-2xl mx-auto">
      <CreateSlip />
    </div>
  );
}

interface Toast { kind: 'success' | 'error' | 'info'; message: string; }

interface ActiveJob {
  JobID: string;
  PrinterID: string;
  Status: 'QUEUED' | 'LEASED';
  Copies: number;
  SlipCode: string | null;
  JobNumber: string | null;
}

/**
 * Bottom-right panel: every print of mine that is still waiting or
 * printing, each with a Stop button.  It polls the server rather than
 * tracking jobs locally, so it survives a page reload and covers prints
 * sent from another tab.  When a job drops off the list we look up how it
 * ended and report that through `notify`.
 */
function ActivePrints({ nonce, notify }: { nonce: number; notify: (t: Toast) => void }) {
  const [rows, setRows] = useState<ActiveJob[]>([]);
  const [stopping, setStopping] = useState<string | null>(null);
  const seen = useRef(new Map<string, ActiveJob>());
  const notifyRef = useRef(notify);
  notifyRef.current = notify;

  useEffect(() => {
    let alive = true;
    const reportOutcome = async (j: ActiveJob) => {
      try {
        const r = await api<{ Status: string; LastError: string | null }>(
          `/api/print/jobs/${encodeURIComponent(j.JobID)}`,
        );
        const what = `${j.Copies} label${j.Copies === 1 ? '' : 's'}${j.SlipCode ? ` (slip ${j.SlipCode})` : ''}`;
        if (r.Status === 'PRINTED') notifyRef.current({ kind: 'success', message: `Printed ${what}` });
        else if (r.Status === 'CANCELLED') notifyRef.current({ kind: 'info', message: `Stopped ${what} — check how many came out` });
        else if (r.Status === 'FAILED') notifyRef.current({ kind: 'error', message: `Print failed: ${r.LastError ?? 'unknown error'}` });
      } catch { /* outcome is also visible in the Print Log */ }
    };
    const tick = async () => {
      try {
        const r = await api<{ rows: ActiveJob[] }>('/api/print/jobs/active');
        if (!alive) return;
        const now = new Map(r.rows.map((j) => [j.JobID, j]));
        for (const [id, j] of seen.current) if (!now.has(id)) void reportOutcome(j);
        seen.current = now;
        setRows(r.rows);
      } catch { /* transient — next tick retries */ }
    };
    void tick();
    const timer = window.setInterval(tick, 2500);
    return () => { alive = false; window.clearInterval(timer); };
  }, [nonce]);

  async function stop(j: ActiveJob) {
    setStopping(j.JobID);
    try {
      const r = await api<{ ok: boolean; wasPrinting?: boolean }>(
        `/api/print/jobs/${encodeURIComponent(j.JobID)}/cancel`, { method: 'POST' },
      );
      notify(r.wasPrinting
        ? { kind: 'info', message: 'Stopping — the printer halts within a label or two' }
        : { kind: 'info', message: 'Cancelled before printing started' });
    } catch (e) {
      notify({ kind: 'error', message: `Stop failed: ${(e as Error)?.message || 'unknown error'}` });
    } finally {
      setStopping(null);
    }
  }

  if (rows.length === 0) return null;
  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 space-y-2">
      {rows.map((j) => (
        <div key={j.JobID} className="flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 shadow-lg text-sm">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-amber-900">
              {j.Status === 'LEASED' ? 'Printing' : 'Waiting for printer'} · {j.Copies} label{j.Copies === 1 ? '' : 's'}
            </div>
            <div className="truncate text-xs text-amber-800">
              {[j.SlipCode && `Slip ${j.SlipCode}`, j.JobNumber && `JC ${j.JobNumber}`, j.PrinterID].filter(Boolean).join(' · ')}
            </div>
          </div>
          <button
            type="button"
            disabled={stopping === j.JobID}
            onClick={() => stop(j)}
            className="rounded bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
          >
            {stopping === j.JobID ? 'Stopping…' : 'Stop'}
          </button>
        </div>
      ))}
    </div>
  );
}

function CreateSlip() {
  const [jobId, setJobId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [slipDate, setSlipDate] = useState(() => new Date().toISOString().split('T')[0] ?? '');
  const [shift, setShift] = useState('DAY');
  const [operator, setOperator] = useState('');
  const [qc, setQc] = useState('');
  const [machine, setMachine] = useState('');
  const [remark, setRemark] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastSlip, setLastSlip] = useState<SlipResult | null>(null);
  // Bumped after each "Send to Printer" so the printing panel refreshes
  // immediately instead of waiting for its next poll.
  const [printNonce, setPrintNonce] = useState(0);
  const [copies, setCopies] = useState(6);
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = (t: Toast, ms = 4000) => {
    setToast(t);
    if (ms > 0) window.setTimeout(() => setToast((cur) => (cur === t ? null : cur)), ms);
  };

  // Registered label printers.  Empty until an agent is installed; the
  // "Send to Printer" button is disabled while the list is empty and the
  // operator can fall back to the browser Print / Save PDF buttons.
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [printerId, setPrinterId] = useState<string>(() =>
    typeof localStorage !== 'undefined' ? localStorage.getItem('kpjm.slip.printerId') ?? '' : '');

  useEffect(() => {
    api<{ rows: Printer[] }>('/api/print/agents')
      .then(r => setPrinters(r.rows))
      .catch(() => setPrinters([]));
  }, []);

  useEffect(() => {
    try { localStorage.setItem('kpjm.slip.printerId', printerId); } catch { /* ignore */ }
  }, [printerId]);

  // Editable job-derived fields.  Pre-filled from the job lookup; operator
  // can correct any of them before creating the slip.
  const [clientName, setClientName] = useState('');
  const [poNumber, setPoNumber]     = useState('');
  const [itemCode, setItemCode]     = useState('');
  const [materialCode, setMaterialCode] = useState('');
  const [location, setLocation]     = useState('');
  const [jobName, setJobName]       = useState('');

  // Track whether we've filled the fields for the current jobId so we can
  // safely overwrite them on a fresh lookup without wiping user edits mid-type.
  const [filledForJobId, setFilledForJobId] = useState<string>('');

  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewStatus, setPreviewStatus] = useState<string>('');

  // Debounce the job lookup so we don't hammer the API on every keystroke.
  useEffect(() => {
    const id = jobId.trim();
    if (!id) {
      setPreviewError(null); setPreviewLoading(false); setPreviewStatus('');
      // Clear derived fields when the operator empties the Job ID.
      setClientName(''); setPoNumber(''); setItemCode(''); setMaterialCode(''); setLocation(''); setJobName('');
      setFilledForJobId('');
      return;
    }
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const r = await api<JobPreview>(`/api/job-orders/${encodeURIComponent(id)}/print`);
        // Pre-fill all four editable fields.  If the operator already edited
        // them for THIS same jobId, don't stomp their changes.
        if (filledForJobId !== id) {
          setClientName(r.Client_Name ?? '');
          setPoNumber(r.PO_Number ?? '');
          setItemCode(r.Product_AW_Material_No ?? '');
          setLocation(r.Client_Address_Dispatch ?? '');
          setJobName(r.Job_Name ?? '');
          setFilledForJobId(id);
        }
        setPreviewStatus(r.Job_Staus ?? '');
      } catch (e: unknown) {
        const msg = (e as Error)?.message ?? 'lookup_failed';
        setPreviewError(msg === 'not_found' ? 'No job card matches that number.' : 'Job lookup failed.');
        setFilledForJobId('');
      } finally {
        setPreviewLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [jobId, filledForJobId]);

  /**
   * One button = one create + output.
   *
   * 'print' / 'download' render the label in the browser (html2pdf → iframe
   * print, or file download).  'queue' hands the label payload to the
   * shop-floor print agent via /api/print/jobs — the label physically
   * prints on the chosen printer without opening a browser dialog.
   *
   * All three modes create the slip row first so the SlipCode is
   * generated exactly once regardless of output path.
   */
  async function handleSubmit(mode: 'print' | 'download' | 'queue') {
    if (!jobId || busy) return;
    if (mode === 'queue' && !printerId) {
      alert('Choose a printer first.');
      return;
    }
    setBusy(true);
    try {
      const dateParts = slipDate.split('-');
      const formattedDate = dateParts.length === 3 ? `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}` : slipDate;

      const result = await api<SlipResult>('/api/slips', {
        method: 'POST',
        body: JSON.stringify({
          jobId: jobId.trim(),
          quantity,
          slipDate: formattedDate,
          shift,
          operator,
          qc,
          machine,
          remark,
          clientName,
          poNumber,
          itemCode,
          materialCode,
          location,
        }),
      });
      setLastSlip(result);

      const lookup = await api<LookupResult>(`/api/slips/lookup/${result.slipCode}`);
      // The shop floor only reads the trailing serial ("81477") — strip
      // the "VP/092026/" prefix so the label carries just that number.
      // If a batch number was entered, append it as "81477/BATCH01"; the
      // label field header changes to "JC / BATCH NO." to match.
      const shortJobId = (lookup.jobId || '').split('/').pop() || '';
      const trimmedBatch = batchNumber.trim();
      const jcBatchValue = trimmedBatch ? `${shortJobId}/${trimmedBatch}` : shortJobId;
      const payload = {
        slipCode: lookup.slipCode,
        jobId: jcBatchValue,
        jobName: lookup.jobName || '',
        client: lookup.client || '',
        poNumber: lookup.poNumber || '',
        itemCode: lookup.itemCode || '',
        materialCode: lookup.materialCode || '',
        quantity: lookup.quantity || quantity,
        location: lookup.location || '',
        slipDate: lookup.slipDate || '',
        shift: lookup.shift || '',
        machine: lookup.machine || '',
        qc: lookup.qc || '',
        operator: lookup.operator || '',
        remark: lookup.remark || '',
      };

      if (mode === 'queue') {
        // The idempotency key ties the queue row to this specific slip so
        // a double-click (network retry, jittery button) enqueues once.
        await api<{ jobId: string; status: string }>('/api/print/jobs', {
          method: 'POST',
          body: JSON.stringify({
            printerId,
            templateName: 'packing_slip_4x4',
            payload,
            copies,
            idempotencyKey: `slip:${lookup.slipCode}`,
          }),
        });
        // Don't block the form while it prints — the printing panel tracks
        // the job, offers Stop, and reports how it ended.
        showToast({ kind: 'info', message: `Sent ${copies} slip(s) to ${printerId}` });
        setPrintNonce((n) => n + 1);
      } else {
        await generateStickerLabel(payload, copies, mode);
        showToast({ kind: 'success', message: mode === 'print' ? 'Sent to browser Print' : 'PDF saved to Downloads' });
      }

      // Reset the form for the next slip.
      setJobId('');
      setLastSlip(null);
      setClientName(''); setPoNumber(''); setItemCode(''); setMaterialCode(''); setLocation(''); setJobName('');
      setBatchNumber('');
      setFilledForJobId('');
    } catch (err: unknown) {
      showToast({ kind: 'error', message: (err as Error)?.message || 'Failed to create packaging slip' }, 6000);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      {toast && (
        <div
          className={`fixed top-4 right-4 px-4 py-3 rounded-lg shadow-lg text-sm z-50 border ${
            toast.kind === 'success' ? 'bg-green-50 text-green-800 border-green-300' :
            toast.kind === 'error'   ? 'bg-red-50 text-red-800 border-red-300' :
                                       'bg-blue-50 text-blue-800 border-blue-300'
          }`}
          role="status"
        >
          {toast.message}
        </div>
      )}
      <h1 className="text-xl font-bold mb-6">Create Packaging Slip</h1>

      <ActivePrints nonce={printNonce} notify={(t) => showToast(t, t.kind === 'error' ? 8000 : 5000)} />

      {lastSlip && !busy && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
          <span className="text-green-800 font-semibold">Packaging slip {lastSlip.slipCode}</span>
          <span className="text-green-700 ml-2">created for {lastSlip.jobId}</span>
        </div>
      )}

      <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
        <div className="space-y-1">
          <label className="label">Job Card Number *</label>
          <input
            type="text"
            className="input text-lg font-bold"
            value={jobId}
            onChange={e => setJobId(e.target.value)}
            placeholder="e.g. 81488"
            required
            autoFocus
          />

          {jobId.trim() && previewLoading && (
            <div className="mt-2 text-xs text-slate-500">Looking up job…</div>
          )}
          {jobId.trim() && previewError && (
            <div className="mt-2 text-xs text-red-600 font-medium">{previewError}</div>
          )}
          {jobId.trim() && !previewError && filledForJobId && previewStatus && (
            <div className="mt-1 text-[10px] uppercase tracking-wider text-slate-500">Job status: {previewStatus}</div>
          )}
        </div>

        {/* Editable job-derived fields.  Everything below the Job Card Number
            arrives pre-filled from the job lookup; the operator can tweak
            any of them before the slip is created + printed. */}
        <div className="space-y-1">
          <label className="label">Job Name</label>
          <input type="text" className="input" value={jobName} onChange={e => setJobName(e.target.value)} placeholder="Auto-filled from job" />
        </div>

        <div className="space-y-1">
          <label className="label">Client Name</label>
          <input type="text" className="input" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Auto-filled from client master" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="label">PO Number</label>
            <input type="text" className="input" value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="Auto-filled from PO" />
          </div>
          <div className="space-y-1">
            <label className="label">Item Code</label>
            <input type="text" className="input" value={itemCode} onChange={e => setItemCode(e.target.value)} placeholder="A/W Material No." />
          </div>
          <div className="space-y-1">
            <label className="label">Material Code</label>
            <input type="text" className="input" value={materialCode} onChange={e => setMaterialCode(e.target.value)} placeholder="Prints only when filled" />
          </div>
        </div>

        <div className="space-y-1">
          <label className="label">Location (dispatch address)</label>
          <textarea
            className="input min-h-[60px]"
            value={location}
            onChange={e => setLocation(e.target.value)}
            placeholder="Auto-filled from client master; edit if needed"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="label">Date</label>
            <input type="date" className="input" value={slipDate} onChange={e => setSlipDate(e.target.value)} required />
          </div>
          <div className="space-y-1">
            <label className="label">Shift</label>
            <select className="input" value={shift} onChange={e => setShift(e.target.value)}>
              <option value="DAY">DAY</option>
              <option value="NIGHT">NIGHT</option>
            </select>
          </div>
        </div>

        <div className="space-y-1">
          <label className="label">Quantity</label>
          <input type="text" className="input" value={quantity} onChange={e => setQuantity(e.target.value)} placeholder="Leave blank to handwrite later" />
        </div>

        <div className="space-y-1">
          <label className="label">Operator & Sorter</label>
          <input type="text" className="input" value={operator} onChange={e => setOperator(e.target.value)} placeholder="e.g. RAJ KUMAR /MURATI" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="label">QC</label>
            <input type="text" className="input" value={qc} onChange={e => setQc(e.target.value)} placeholder="e.g. ROHIT SINGH" />
          </div>
          <div className="space-y-1">
            <label className="label">Machine</label>
            <select className="input" value={machine} onChange={e => setMachine(e.target.value)}>
              <option value="">— Select —</option>
              <option value="FG01-Acme">FG01-Acme</option>
              <option value="FG02-Acme">FG02-Acme</option>
              <option value="FG03-ET">FG03-ET</option>
              <option value="FG04-PR">FG04-PR</option>
            </select>
          </div>
        </div>

        <div className="space-y-1">
          <label className="label">Batch Number</label>
          <input
            type="text"
            className="input"
            value={batchNumber}
            onChange={e => setBatchNumber(e.target.value)}
            placeholder="Optional — prints as JOB/BATCH on the label"
          />
        </div>

        <div className="space-y-1">
          <label className="label">Remark</label>
          <input type="text" className="input" value={remark} onChange={e => setRemark(e.target.value)} placeholder="Optional note" />
        </div>

        {printers.length > 0 && (
          <div className="space-y-1">
            <label className="label">Send to printer</label>
            <select
              className="input"
              value={printerId}
              onChange={e => setPrinterId(e.target.value)}
            >
              <option value="">— Browser Print / Save PDF —</option>
              {printers.map(p => {
                const stale = !p.LastHeartbeatOn
                  || (Date.now() - new Date(p.LastHeartbeatOn).getTime()) > 90_000;
                return (
                  <option key={p.PrinterID} value={p.PrinterID}>
                    {p.DisplayName || p.PrinterID}
                    {p.Location ? ` — ${p.Location}` : ''}
                    {stale ? ' (offline)' : ''}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        <div className="pt-4 flex items-center gap-3">
          <label className="text-sm font-medium text-gray-600 shrink-0">Copies:</label>
          <input
            type="number"
            min={1}
            max={250}
            value={copies}
            onChange={e => setCopies(Math.max(1, Math.min(250, +e.target.value || 1)))}
            className="input w-20"
          />
          {printerId ? (
            <button
              type="button"
              className="btn-primary flex-1 py-3"
              disabled={busy || !jobId || !!previewError || previewLoading}
              onClick={() => handleSubmit('queue')}
            >
              {busy ? 'Sending…' : 'Send to Printer'}
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary flex-1 py-3"
              disabled={busy || !jobId || !!previewError || previewLoading}
              onClick={() => handleSubmit('print')}
            >
              {busy ? 'Working…' : 'Print'}
            </button>
          )}
          <button
            type="button"
            className="btn-ghost flex-1 py-3 border border-slate-300"
            disabled={busy || !jobId || !!previewError || previewLoading}
            onClick={() => handleSubmit('download')}
          >
            {busy ? 'Working…' : 'Save PDF'}
          </button>
        </div>
      </form>
    </div>
  );
}

